import isEqual from 'lodash.isequal';
import type { DirectiveBinding } from 'vue';
import type InputmaskType from 'inputmask';


interface ExtendedHTMLInputElement extends HTMLInputElement {
  unsubscribe?: () => void;
  inputmask?: InputmaskType.Instance;
}

type InputmaskCtor = typeof InputmaskType;

let inputmaskPromise: Promise<InputmaskCtor> | null = null;
let InputmaskClass: InputmaskCtor | null = null;

function loadInputmask(): Promise<InputmaskCtor> {
  if (InputmaskClass) {
    return Promise.resolve(InputmaskClass);
  }

  if (!inputmaskPromise) {
    inputmaskPromise = import('inputmask').then((mod) => {
      const Inputmask = mod.default;

      Inputmask.extendDefaults({
        autoUnmask: true,
        showMaskOnHover: false,
      });

      Inputmask.extendDefinitions({
        // переопределяем стандартный definition чтобы пропускал только латиницу
        a: {
          validator: '[A-Za-z]',
        },
      });

      Inputmask.extendAliases({
        phone: {
          mask: '+7 (999) 999-99-99',
        },
      });

      InputmaskClass = Inputmask;

      return Inputmask;
    });
  }

  return inputmaskPromise;
}

/**
 * Для применения маски на инпуте нужно добавить директиву и указать аргументом alias нужной маски,
 * либо передачей объекта с конфигурацией inputmask
 * @see {@link https://github.com/RobinHerbots/Inputmask|Inputmask}
 *
 * v-mask:phone
 * v-mask:date
 *
 * v-mask="{ alias: 'phone' }"
 * v-mask="{ alias 'date' }"
 *
 * так же работает динамическое изменение маски (только при использовании объекта в качесте значения директивы)
 * @see {@link https://vuejs.org/guide/reusability/custom-directives.html#directive-hooks|Аргументы функций-обработчиков состояний директивы}
 * const someValue = { alias: 'phone' };
 * ...
 * v-mask="someValue"
 */
export default {
  created: setupInputmask,

  updated(el: ExtendedHTMLInputElement, bindings: DirectiveBinding) {
    const { value: params, oldValue: oldParams } = bindings;

    const isParamsUpdated = !isEqual(params, oldParams);

    if (isParamsUpdated) {
      el.value = '';

      const syntEvent = new Event('input', {
        bubbles: true,
        cancelable: true,
      });

      el.dispatchEvent(syntEvent);

      if (el.inputmask) {
        el.inputmask.option(params);
      } else {
        setupInputmask(el, bindings);
      }
    }
  },

  unmounted(el: ExtendedHTMLInputElement) {
    if (typeof el.unsubscribe === 'function') {
      el.unsubscribe();
    }

    if (el.inputmask && typeof el.inputmask.remove === 'function') {
      el.inputmask.remove();
    }
  },
};

/**
 * Функция для проверки существования необходимого alias
 *
 * @param {string} aliasName - имя alias маски
 * @returns {boolean}
 */
function isAliasExist(Inputmask: InputmaskCtor, aliasName: string): boolean {
  return !!Inputmask.prototype.aliases[aliasName];
}

/**
 * Функция для инициализации экзкмпляра Inputmask и добевления его к элементу
 * @see {@link https://vuejs.org/guide/reusability/custom-directives.html#directive-hooks|Хуки}
 *
 * @param {ExtendedHTMLInputElement} el - объект елемента
 * @param {DirectiveBinding} bindings - аргумент хука vue-директивы
 * @param {string | undefined} bindings.arg - аргумент передаваемый при добавлении директивы v-mask:arg
 */

function setupInputmask(el: HTMLElement, bindings: DirectiveBinding) {
  void loadInputmask().then((Inputmask) => {
    let targetElement: ExtendedHTMLInputElement;

    if (el.tagName.toLowerCase() === 'input') {
      targetElement = el as ExtendedHTMLInputElement;
    } else {
      targetElement = el.getElementsByTagName('input')[0] as ExtendedHTMLInputElement;
    }

    const { arg: alias, value } = bindings;

    if (targetElement && value) {
      new Inputmask(value).mask(targetElement);

      addEventHandlers(targetElement);
    } else if (targetElement && alias && isAliasExist(Inputmask, alias)) {
      new Inputmask({ alias }).mask(targetElement);

      addEventHandlers(targetElement);
    }
  });
}

/**
 * Функция для добавления обработчиков событий на элемент
 *
 * @param {ExtendedHTMLInputElement} el - элемент страницы к которому добавляется директива
 */
function addEventHandlers(el: ExtendedHTMLInputElement) {
  if (typeof el.unsubscribe === 'function') {
    return;
  }

  const repairSync = () => {
    if (el.value) {
      const syntEvent = new Event('input', {
        bubbles: true,
        cancelable: true,
      });

      el.dispatchEvent(syntEvent);
    }
  };

  const transitionHandler = (event: TransitionEvent) => {
    if (event.propertyName === 'background-color') {
      setTimeout(repairSync, 0);
    }
  };

  const animationHandler = (event: AnimationEvent) => {
    if (event.animationName === 'onautofillstart') {
      setTimeout(repairSync, 0);
    }
  };

  const pasteHandler = () => {
    setTimeout(repairSync, 0);
  };

  el.addEventListener('transitionstart', transitionHandler, true);
  el.addEventListener('animationstart', animationHandler, true);
  el.addEventListener('paste', pasteHandler, true);

  el.unsubscribe = () => {
    el.removeEventListener('transitionstart', transitionHandler, true);
    el.removeEventListener('animationstart', animationHandler, true);
    el.removeEventListener('paste', pasteHandler, true);
  };
}
